import { useEffect } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import clsx from "clsx";

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer = null,
  size = "md",
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = "",
}) => {
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, closeOnEscape, onClose]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "";
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose?.();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={clsx(
          "relative z-10 w-full rounded-2xl bg-white shadow-2xl",
          sizeClasses[size] || sizeClasses.md,
          className
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#ead8c0] px-6 py-4">
          <div>
            {title && (
              <h2
                id="modal-title"
                className="text-xl font-semibold text-[#8B3A3A]"
              >
                {title}
              </h2>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#6b5b57] transition hover:bg-[#f7f1e8] hover:text-[#8B3A3A]"
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="border-t border-[#ead8c0] px-6 py-4">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;